// usePlan.js — 사용자 플랜 상태 관리 (V99: 평생 이용권 단건 결제 모델)
import { useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from './db';

export const PLANS = {
  free:       { label: '무료',      price: 0,     color: '#6b7280' },
  pro:        { label: '평생 Pro',  price: 39000, color: '#EA580C' },
  franchise:  { label: '프랜차이즈', price: null,  color: '#7C3AED' },
};

// ── 평생 이용권 가격 (단건 결제, 1회) ──
export const LIFETIME_PRICE = 50000; // (구 Toss 직접결제 경로용 — 현재 미사용)

// ── 래피드(latpeed) 판매 정보 (V101) ──
export const LATPEED_URL   = 'https://www.latpeed.com/products/VVpRs';
export const LAUNCH_PRICE  = 39000;  // 런칭 특가 (30명 한정)
export const REGULAR_PRICE = 59000;  // 이후 정가

// ── 무료 체험 (V102) ──
// 가입일 기준 3일간 전 기능(Pro) 무료 체험.
// 체험 종료 후에는 이용권 코드 등록(평생 Pro) 시에만 사용 가능 — 앱 전체 잠금.
export const TRIAL_DAYS = 3;
const DAY_MS = 24 * 60 * 60 * 1000;

const ADMIN_EMAIL = 'u012me@gmail.com';
const norm = (e) => String(e || '').trim().toLowerCase();

// Auth 계정 생성 시각 기준 체험 종료 시각 (ms). 알 수 없으면 null(체험 없음 취급).
export function getTrialEnd(user) {
  const t = user?.metadata?.creationTime ? Date.parse(user.metadata.creationTime) : NaN;
  return Number.isFinite(t) ? t + TRIAL_DAYS * DAY_MS : null;
}

export function usePlan(user) {
  const [subStatus, setSubStatus]   = useState(null);
  const [subLoading, setSubLoading] = useState(true);
  // V116: 관리자 승인 목록(config/allowlist)에 있는 계정 = Pro.
  // 아직 판매 전이라 승인한 사람은 체험 기간·이용권 없이 바로 쓴다.
  const [approved, setApproved]     = useState(false);
  const [allowLoading, setAllowLoading] = useState(true);

  useEffect(() => {
    if (!user) { setSubStatus(null); setSubLoading(false); return; }
    setSubLoading(true);
    const ref = doc(db, 'users', user.uid);
    const unsub = onSnapshot(ref, (snap) => {
      const data = snap.exists() ? snap.data() : {};
      const sub = data.subscription;
      // 평생 이용권 구매자 (lifetime) 또는 admin이 명시적으로 부여한 플랜
      setSubStatus(sub && sub.status === 'active' && sub.plan ? sub : null);
      setSubLoading(false);
    });
    return unsub;
  }, [user]);

  useEffect(() => {
    if (!user) { setApproved(false); setAllowLoading(false); return; }
    const email = norm(user.email);
    if (email === ADMIN_EMAIL) { setApproved(true); setAllowLoading(false); return; }
    setAllowLoading(true);
    const unsub = onSnapshot(doc(db, 'config', 'allowlist'), (snap) => {
      const emails = snap.exists() ? (snap.data().emails || []) : [];
      setApproved(!!email && emails.some(e => norm(e) === email));
      setAllowLoading(false);
    }, (err) => {
      // 로그인 관문(checkAllowlist)과 같은 원칙: 네트워크 문제로 매장을 잠그지 않는다 (fail-open).
      // 허페이 매장은 VPN 경유라 Firestore 읽기가 종종 실패한다.
      console.error('승인 목록 확인 실패:', err);
      setApproved(true);
      setAllowLoading(false);
    });
    return unsub;
  }, [user]);

  const loading = subLoading || allowLoading;

  // 평생 이용권 보유 여부
  const isLifetime = !!(subStatus && subStatus.lifetime && subStatus.status === 'active');

  // admin이 부여한 플랜 포함, 유료 플랜 보유 여부
  const hasPaidPlan = !!(subStatus && subStatus.status === 'active' && subStatus.plan && subStatus.plan !== 'free');

  // 승인으로 Pro를 쓰는 중 (유료 플랜이 없는 승인 계정)
  const isApproved = approved && !hasPaidPlan;

  // 체험 상태 계산 — 승인 계정은 체험·잠금 대상이 아니다
  const trialEnd      = user ? getTrialEnd(user) : null;
  const now           = Date.now();
  const trialActive   = !hasPaidPlan && !approved && !!trialEnd && now < trialEnd;
  const trialExpired  = !hasPaidPlan && !approved && (!trialEnd || now >= trialEnd);
  const trialDaysLeft = trialActive ? Math.max(1, Math.ceil((trialEnd - now) / DAY_MS)) : 0;

  let plan = subStatus ? subStatus.plan : (trialActive ? 'pro' : 'free');
  if (approved && plan === 'free') plan = 'pro';

  // 플랜 접근 권한 체크
  const canAccess = (requiredPlan) => {
    const order = ['free', 'pro', 'franchise'];
    return order.indexOf(plan) >= order.indexOf(requiredPlan);
  };

  return { plan, subStatus, loading, canAccess, isLifetime, isApproved, trialActive, trialExpired, trialDaysLeft, trialEnd };
}
